import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './CreateWorkspace.css';

const COMPANY_SIZES = [
    '1-10 employees',
    '11-50 employees',
    '51-200 employees',
    '201-1000 employees',
    '1000+ employees'
];

function CreateWorkspace() {
    const navigate = useNavigate();
    const { user, refreshUser } = useAuth();
    const [workspaceName, setWorkspaceName] = useState('');
    const [companySize, setCompanySize] = useState('51-200 employees');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Generate slug preview
    const generateSlug = (name) => {
        if (!name) return 'your-workspace-slug';
        return name
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-|-$/g, '') || 'workspace';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!workspaceName.trim()) {
            setError('Please enter a workspace name');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await api.post('/workspaces', {
                name: workspaceName.trim(),
                companySize
            });

            // Refresh user data to get the new workspace
            await refreshUser();

            // Redirect to dashboard
            navigate('/');
        } catch (err) {
            console.error('Workspace creation error:', err);
            setError(err.response?.data?.error || 'Failed to create workspace');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="create-workspace-container">
            <div className="create-workspace-header">
                <div className="logo">
                    <img
                        src={`${process.env.PUBLIC_URL}/rova-ai-logo-wordmark-white.png`}
                        alt="ROVA AI"
                        className="logo-wordmark"
                        style={{ height: '32px', width: 'auto' }}
                    />
                </div>
                <div className="user-avatar">
                    <div className="avatar-circle">
                        {user?.fullName?.[0] || user?.email?.[0] || 'U'}
                    </div>
                </div>
            </div>

            <div className="create-workspace-content">
                <div className="create-workspace-card">
                    <h1>Create Your First Workspace</h1>
                    <p className="subtitle">
                        Set up your workspace and start automating your mobile testing with AI.
                    </p>

                    <form onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label htmlFor="workspaceName">Workspace Name</label>
                            <input
                                type="text"
                                id="workspaceName"
                                value={workspaceName}
                                onChange={(e) => setWorkspaceName(e.target.value)}
                                placeholder="Acme QA Team"
                                className="workspace-input"
                                autoFocus
                            />

                        </div>

                        <div className="form-group">
                            <label htmlFor="companySize">Company/Team Size</label>
                            <div className="select-wrapper">
                                <select
                                    id="companySize"
                                    value={companySize}
                                    onChange={(e) => setCompanySize(e.target.value)}
                                    className="workspace-select"
                                >
                                    {COMPANY_SIZES.map((size) => (
                                        <option key={size} value={size}>
                                            {size}
                                        </option>
                                    ))}
                                </select>
                                <svg className="select-arrow" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                        </div>

                        {error && (
                            <div className="error-message">
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            className="launch-button"
                            disabled={loading || !workspaceName.trim()}
                        >
                            {loading ? (
                                <>
                                    <div className="spinner"></div>
                                    Creating...
                                </>
                            ) : (
                                <>
                                    Launch Workspace
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                        <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </>
                            )}
                        </button>

                        <p className="settings-note">
                            You can change these settings later in your workspace settings.
                        </p>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default CreateWorkspace;
